import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { ShipmentLocation, StoneAttribute, StoneOptions } from '@stone/interfaces';

interface MetaState {
  options: StoneOptions;
  /** Factory locations (from `/stone/warehouseFactories`) used by Inventory / Locations filters. */
  warehouses: StoneAttribute[];
  /**
   * Logistic destination locations (from `/stone/logistic/locations` with type=warehouse).
   * Mirrors `state.stone.reports.purchaseLocations` (type!=warehouse) — shipment flows use
   * these for the `to_location_id` picker.
   */
  warehouseLocations: {
    items: ShipmentLocation[];
    loading: boolean;
  };
  carriers: StoneAttribute[];
  loading: boolean;
  error: string | null;
}

const initialState: MetaState = {
  options: {
    shapes: [],
    stone_types: [],
    qualities: [],
    brillant_colours: [],
    cuts: [],
    certifications: [],
    clarities: [],
  },
  warehouses: [],
  warehouseLocations: { items: [], loading: false },
  carriers: [],
  loading: false,
  error: null,
};

const metaSlice = createSlice({
  name: 'stone.meta',
  initialState,
  reducers: {
    fetchOptionsRequest: (state, _action: PayloadAction<any | undefined>) => {
      state.loading = true;
      state.error = null;
    },
    fetchOptionsSuccess: (state, action: PayloadAction<{ options: StoneOptions; warehouses: StoneAttribute[]; carriers: StoneAttribute[] }>) => {
      state.options = action.payload.options;
      state.warehouses = action.payload.warehouses;
      state.carriers = action.payload.carriers;
      state.loading = false;
    },
    fetchOptionsFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },
    fetchWarehouseLocationsRequest: (state, _action: PayloadAction<any>) => {
      state.warehouseLocations.loading = true;
      state.error = null;
    },
    fetchWarehouseLocationsSuccess: (state, action: PayloadAction<ShipmentLocation[]>) => {
      state.warehouseLocations.items = action.payload;
      state.warehouseLocations.loading = false;
    },
    fetchWarehouseLocationsFailure: (state, action: PayloadAction<string>) => {
      state.warehouseLocations.loading = false;
      state.error = action.payload;
    },
  },
});

export const {
  fetchOptionsRequest,
  fetchOptionsSuccess,
  fetchOptionsFailure,
  fetchWarehouseLocationsRequest,
  fetchWarehouseLocationsSuccess,
  fetchWarehouseLocationsFailure,
} = metaSlice.actions;
export default metaSlice.reducer;
